import { TimeseriesStateManager } from "~/modules/visualization/components/Timeseries/TimeseriesStateManager";
import { TimeseriesCard } from "~/modules/visualization/components/Timeseries/TimeseriesCard";
import { TimeSeriesChart } from "~/modules/visualization/components/TimeSeriesChartV3/broadridge_chart";
import { getSideBySideColumnChart } from "./modes";
import { MEASURE_SLUGS } from "~/consts";
import {
    ChartTypes,
    TimeSeriesItem,
    TimeSeriesUnits,
} from "~/modules/token_timeseries/types";
import { QueryResponse } from "~/modules/filters/types";
import { ColumnSpanConfig } from "~/types";
import { YAxisTypes } from "~/modules/visualization/components/Timeseries/types";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";

// // // //

/**
 * BroadridgeTimeSeriesCard
 * Encapsulates markup for the Broadridge Time Series card with dual-axis support
 */
export function BroadridgeTimeSeriesCard(props: {
    title?: string;
    columnSpanConfig?: ColumnSpanConfig;
    totalContractsTimeseries: QueryResponse<TimeSeriesItem>;
    totalCollateralizedFundsTimeseries: QueryResponse<TimeSeriesItem>;
    totalIssuedFundsTimeseries: QueryResponse<TimeSeriesItem>;
}) {
    const {
        title = "Broadridge DLR Metrics",
        totalContractsTimeseries,
        totalCollateralizedFundsTimeseries,
        totalIssuedFundsTimeseries,
        columnSpanConfig = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
        },
    } = props;

    // Define VisualizationModes - keeping for backward compatibility
    // const TOTAL_CONTRACTS = getTotalContractsChart({
    //     key: "broadridge-total-contrats",
    //     label: "Total Contracts",
    //     description: "Total number of contracts",
    //     filters: [],
    //     defaultGroupBy: AssetTimeseriesGroupBy.protocol,
    //     enableGroupByControls: false,
    //     measureID: MEASURE_IDS.TotalContracts,
    // });

    const TOTAL_ISSUED_FUNDS = getSideBySideColumnChart({
        key: "broadridge-total-repo-volumes",
        label: "Repo Volumes",
        description: "Total value of repo volumes",
        filters: [],
        defaultGroupBy: TokenTimeseriesGroupBy.protocol,
        enableGroupByControls: false,
        measureSlug: MEASURE_SLUGS.total_issued_funds,
    });

    const modes = [TOTAL_ISSUED_FUNDS];

    function getDualAxisSeries() {
        // Combine all series with proper axis assignment
        const repoVolumesSeries = [
            ...totalCollateralizedFundsTimeseries.results,
            ...totalIssuedFundsTimeseries.results,
        ].map((each) => ({
            item: each,
            enabled: true,
            chartType: ChartTypes.column,
            yAxis: 0 as const, // Primary axis (left) for repo volumes
        }));

        const contractsSeries = totalContractsTimeseries.results.map(
            (each) => ({
                item: each,
                enabled: true,
                chartType: ChartTypes.line,
                yAxis: 1 as const, // Secondary axis (right) for contracts
            }),
        );

        return [...repoVolumesSeries, ...contractsSeries];
    }

    // Calculate dynamic scaling to align line and columns visually
    const getScaledAxisConfig = () => {
        // Get max values for scaling with safety checks
        const contractValues = totalContractsTimeseries.results.flatMap(
            (item) => item.points.map((point) => point[1]),
        );
        const maxContracts =
            contractValues.length > 0 ? Math.max(...contractValues) : 100;

        const repoValues = [
            ...totalCollateralizedFundsTimeseries.results,
            ...totalIssuedFundsTimeseries.results,
        ].flatMap((item) => item.points.map((point) => point[1]));
        const maxRepoVolumes =
            repoValues.length > 0 ? Math.max(...repoValues) : 1000000;

        // Scale the primary axis to bring line closer to columns
        const contractsMax = maxContracts * 1.2; // 20% headroom
        const repoMax = maxRepoVolumes * 1.1; // 10% headroom

        return {
            primary: {
                title: "Repo Volumes",
                unit: TimeSeriesUnits.dollar,
                min: 0,
                max: repoMax,
                enabled: true,
                type: YAxisTypes.linear,
            },
            secondary: {
                title: "Trade Count",
                unit: TimeSeriesUnits.token,
                min: 0,
                max: contractsMax,
                enabled: true,
                type: YAxisTypes.linear,
                opposite: true,
            },
        };
    };

    const dualAxisConfig = getScaledAxisConfig();

    return (
        <TimeseriesStateManager modes={modes}>
            {({
                controls,
                mode,
                aggregateQuery,
                appliedQuery,
                dataExportControls,
            }) => (
                <TimeseriesCard
                    title={title}
                    height={mode.activeMode.visualizationConfig?.height || 400}
                    apiQuery={appliedQuery}
                    loading={false}
                    modeControls={mode}
                    controls={controls}
                    exportData={dataExportControls}
                    apiTools={mode.activeMode.apiTools}
                    colSpans={columnSpanConfig}
                    aggregateQueryControls={{
                        groupBy: aggregateQuery.groupBy,
                        interval: aggregateQuery.interval,
                        aggregateFunction: aggregateQuery.aggregateFunction,
                    }}
                >
                    <TimeSeriesChart
                        isDualAxis={true}
                        enableZoom={true}
                        series={getDualAxisSeries()}
                        navigator={controls.navigator}
                        yAxis={dualAxisConfig}
                        height={
                            mode.activeMode.visualizationConfig?.height || 400
                        }
                        legend={{
                            enabled: controls.legend.active,
                            align: "left",
                            verticalAlign: "top",
                            ...(mode.activeMode.visualizationConfig?.legend ||
                                {}),
                        }}
                    />
                </TimeseriesCard>
            )}
        </TimeseriesStateManager>
    );
}
